import { PaginatedList, Spinner } from "@tellescope/ui";
import { useInboxDrafts } from "../../hooks";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { formatEmailTitle, formatName, formatPhone } from "@tellescope/utils";
import { InboxThreadListDraftItem } from "./list-items";

export const InboxDraftsList = () => {
  const { t } = useTranslation("inbox");
  const { drafts, isLoadingDrafts, endusers, chatrooms, isError } = useInboxDrafts();

  const getItemProps = useCallback(
    (draft: (typeof drafts)[number]) => {
      switch (draft.channel) {
        case "Chat":
          return {
            title: chatrooms?.chatroomsMap[draft.roomId]?.title ?? t("Unknown chat"),
            timestamp: draft.updatedAt,
            preview: draft.message,
            channel: draft.channel,
          };
        case "SMS": {
          const enduser = endusers?.endusersMap[draft.enduserId];
          const title = enduser
            ? formatName(enduser)
            : draft.enduserPhoneNumber
            ? formatPhone(draft.enduserPhoneNumber)
            : t("Unknown number");

          return {
            title,
            timestamp: draft.updatedAt,
            preview: draft.message,
            channel: draft.channel,
          };
        }
        case "Email":
          return {
            title: formatEmailTitle(draft.subject),
            timestamp: draft.updatedAt,
            preview: draft.textContent,
            channel: draft.channel,
          };
      }
    },
    [chatrooms?.chatroomsMap, endusers?.endusersMap, t]
  );

  if (isError) {
    return (
      <div className="p-4 text-center text-muted-foreground">
        <div className="text-red-500 mb-2">{t("Error loading inbox threads")}</div>
      </div>
    );
  }

  if (isLoadingDrafts) {
    return (
      <div className="w-full h-full min-h-screen flex items-center justify-center text-center text-muted-foreground">
        <Spinner />
      </div>
    );
  }

  if (drafts.length === 0) {
    return (
      <div className="flex p-4 h-full text-center text-muted-foreground min-h-screen items-center justify-center">
        {t("No drafts found")}
      </div>
    );
  }

  return (
    <PaginatedList className="p-4 space-y-2 flex-1">
      {drafts.map((draft) => (
        <InboxThreadListDraftItem
          key={draft.id}
          draft={draft}
          {...getItemProps(draft)}
          className="my-1.5"
          enduserIds={draft.enduserId ? [draft.enduserId] : []}
          chatroomsMap={chatrooms?.chatroomsMap ?? {}}
        />
      ))}
    </PaginatedList>
  );
};
